import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFetchSystemBanners } from '../../hooks/toolbox-hooks';
import { SystemBanner } from '../../types/dashboard';
import './SystemBanners.scss';

// Per-user dismissals live in localStorage (per-browser-profile, which is effectively per-user on
// their machine — no server round-trip, and a dismissed banner stays dismissed across reloads). The
// stored value is the banner's `last_updated` at dismiss time, keyed by id: if an admin later edits
// the banner, its `last_updated` changes and the banner re-surfaces, which is the desired behaviour
// for "we changed the message, show it again".
const DISMISS_KEY = 'systemBanner.dismissed.v1';

type DismissMap = Record<string, string>;

function readDismissed(): DismissMap {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? (parsed as DismissMap) : {};
  } catch {
    return {};
  }
}

function writeDismissed(map: DismissMap): void {
  try {
    window.localStorage.setItem(DISMISS_KEY, JSON.stringify(map));
  } catch {
    /* localStorage unavailable (private mode / quota) — dismissal is best-effort. */
  }
}

// Active AND within the (optional) scheduling window, evaluated client-side so a scheduled banner
// appears/disappears without a redeploy. Null bounds are open-ended.
function isLive(banner: SystemBanner, now: number): boolean {
  if (!banner.active) return false;
  if (banner.starts_at) {
    const start = Date.parse(banner.starts_at);
    if (!Number.isNaN(start) && now < start) return false;
  }
  if (banner.ends_at) {
    const end = Date.parse(banner.ends_at);
    if (!Number.isNaN(end) && now > end) return false;
  }
  return true;
}

/**
 * Admin-managed landing-page notifications (db SystemBanner). Replaces the former hardcoded
 * "Frontier AI Workshop" banner: that promo is now the first seeded row and is edited through
 * Admin → Toolbox Configuration → Banners. Severity drives the styling; the schedule window and
 * per-user dismiss are applied here on the client.
 */
const SystemBanners: React.FC = () => {
  const navigate = useNavigate();
  const { data } = useFetchSystemBanners();
  const [dismissed, setDismissed] = useState<DismissMap>(() => readDismissed());

  const now = Date.now();

  const visible = useMemo(() => {
    const banners = Array.isArray(data) ? data : [];
    return banners
      .filter((b) => isLive(b, now))
      .filter((b) => dismissed[String(b.id)] !== (b.last_updated || ''))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [data, dismissed, now]);

  const handleDismiss = useCallback((banner: SystemBanner) => {
    setDismissed((prev) => {
      const next = { ...prev, [String(banner.id)]: banner.last_updated || '' };
      writeDismissed(next);
      return next;
    });
  }, []);

  const handleCta = useCallback(
    (url?: string | null) => {
      if (!url) return;
      if (/^https?:\/\//i.test(url)) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        navigate(url);
      }
    },
    [navigate]
  );

  if (!visible.length) return null;

  return (
    <div className="sysbanner__stack">
      {visible.map((banner) => (
        <section
          key={banner.id}
          className={`sysbanner sysbanner--${banner.severity}`}
          role="region"
          aria-label={banner.title}
        >
          <div className="v2__wrap sysbanner__inner">
            <div className="sysbanner__text">
              {banner.severity === 'promo' && <span className="sysbanner__badge">New</span>}
              <h3 className="sysbanner__title">{banner.title}</h3>
              {banner.body && <p className="sysbanner__desc">{banner.body}</p>}
            </div>

            <div className="sysbanner__actions">
              {banner.cta_label && banner.cta_url && (
                <button
                  type="button"
                  className="sysbanner__cta"
                  onClick={() => handleCta(banner.cta_url)}
                >
                  {banner.cta_label} &rarr;
                </button>
              )}
              <button
                type="button"
                className="sysbanner__dismiss"
                aria-label="Dismiss notification"
                title="Dismiss"
                onClick={() => handleDismiss(banner)}
              >
                &times;
              </button>
            </div>
          </div>
        </section>
      ))}
    </div>
  );
};

export default SystemBanners;
